import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Printer } from "@/components/icons";

export default function PrescriptionPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/prescriptions/${id}`).then((r) => setData(r.data));
  }, [id]);

  // Hide Emergent platform watermark on print pages (this page is outside Layout)
  useEffect(() => {
    const removeBadge = () => {
      document
        .querySelectorAll(
          '#emergent-badge, [id*="emergent-badge"], [class*="emergent-badge"], [class*="MadeWithEmergent"], a[href*="emergent.sh"], a[href*="emergent.com"], a[href*="emergentagent.com"]'
        )
        .forEach((el) => el.remove());
    };
    removeBadge();
    const interval = setInterval(removeBadge, 800);
    window.addEventListener("beforeprint", removeBadge);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeprint", removeBadge);
    };
  }, []);

  if (!data) return <div className="p-10">Cargando…</div>;
  const { prescription, patient, doctor } = data;

  return (
    <div className="min-h-screen bg-[#F5F9F7] p-6 flex flex-col items-center">
      <div className="no-print w-full max-w-3xl flex justify-end mb-4">
        <button
          onClick={() => window.print()}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="print-button"
        >
          <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
        </button>
      </div>

      <style>{`
        @media print {
          /* Ancho A4 exacto (210mm), altura automática según contenido */
          @page { size: 210mm auto; margin: 12mm; }
          html, body { background: #fff !important; }
          body * { visibility: hidden; }
          .print-page, .print-page * { visibility: visible; }
          .print-page {
            position: absolute;
            left: 0; top: 0;
            width: 186mm !important;   /* 210mm - 2 × 12mm de margen */
            max-width: 186mm !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="print-page bg-white rounded-2xl shadow-xl w-full max-w-3xl p-10 text-[#163144]">
        <header className="flex items-center justify-between border-b-2 border-[#1B405B] pb-4">
          <div className="flex items-center gap-3">
            {doctor?.clinic_logo_url ? (
              <img
                src={doctor.clinic_logo_url}
                alt="Logo de la clínica"
                className="w-16 h-16 rounded-2xl object-contain bg-white border border-[#1B405B]/10"
                data-testid="rx-clinic-logo"
              />
            ) : (
              <img
                src="/brand/consudoctor-icon.png"
                alt="ConsuDoctor"
                className="w-14 h-14 rounded-2xl object-contain bg-white border border-[#1B405B]/10"
              />
            )}
            <div>
              <div className="text-2xl font-extrabold tracking-tight">
                {doctor?.name || "ConsuDoctor"}
              </div>
              {doctor?.specialty && (
                <div
                  className="text-xs font-bold text-[#1B405B]"
                  data-testid="rx-doctor-specialty"
                >
                  {doctor.specialty}
                </div>
              )}
              <div className="text-xs uppercase tracking-widest text-[#1B405B]/70">
                Receta médica
              </div>
            </div>
          </div>
          <div className="text-right text-xs">
            <div className="font-bold">
              ID: {prescription.id.slice(0, 8).toUpperCase()}
            </div>
            <div>
              {new Date(prescription.created_at).toLocaleString("es-ES")}
            </div>
          </div>
        </header>

        <section className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
              Médico
            </div>
            <div className="font-bold text-lg" data-testid="rx-doctor-name">
              {doctor?.name}
            </div>
            {doctor?.specialty && (
              <div className="text-[#1B405B] font-bold" data-testid="rx-doctor-specialty-block">
                {doctor.specialty}
              </div>
            )}
            {doctor?.email && <div>{doctor.email}</div>}
            {doctor?.phone && (
              <div data-testid="rx-doctor-phone">Tel: {doctor.phone}</div>
            )}
            {doctor?.address && (
              <div data-testid="rx-doctor-address">{doctor.address}</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
              Paciente
            </div>
            <div className="font-bold text-lg">{patient?.full_name}</div>
            <div>{patient?.document_id}</div>
            <div>{patient?.phone}</div>
          </div>
        </section>

        {/* Diagnóstico intencionalmente omitido de la receta impresa:
            la receta la ve el paciente, la farmacia y potencialmente
            terceros — el dx es información clínica sensible que se queda
            SOLO en la historia clínica del doctor. */}

        <section className="mt-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="text-5xl font-serif italic text-[#1B405B] leading-none select-none">
              ℞
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
                Prescripción
              </div>
              <div className="text-sm text-[#163144]/70">
                {prescription.items.length} medicamento
                {prescription.items.length === 1 ? "" : "s"}
              </div>
            </div>
          </div>

          <table className="w-full text-sm border-collapse" data-testid="rx-items-table">
            <thead>
              <tr className="border-b-2 border-[#1B405B] text-left">
                <th className="py-2 pr-3 text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70 w-8">
                  #
                </th>
                <th className="py-2 px-3 text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
                  Medicamento
                </th>
                <th className="py-2 px-3 text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70 whitespace-nowrap">
                  Dosis
                </th>
                <th className="py-2 px-3 text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70 whitespace-nowrap">
                  Cantidad
                </th>
                <th className="py-2 px-3 text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70 whitespace-nowrap">
                  Frecuencia
                </th>
                <th className="py-2 pl-3 text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70 whitespace-nowrap">
                  Duración
                </th>
              </tr>
            </thead>
            <tbody>
              {prescription.items.map((it, i) => (
                <Fragment key={i}>
                  <tr className="border-b border-[#1B405B]/15 align-top">
                    <td className="py-3 pr-3 font-bold text-[#163144]">
                      {i + 1}.
                    </td>
                    <td className="py-3 px-3 font-extrabold text-[#163144]">
                      {it.medication}
                    </td>
                    <td className="py-3 px-3 text-[#163144]">{it.dosage || "—"}</td>
                    <td className="py-3 px-3 text-[#163144] font-bold">
                      {it.quantity || "—"}
                    </td>
                    <td className="py-3 px-3 text-[#163144]">
                      {it.frequency || "—"}
                    </td>
                    <td className="py-3 pl-3 text-[#163144]">
                      {it.duration || "—"}
                    </td>
                  </tr>
                  {it.instructions && (
                    <tr className="border-b border-[#1B405B]/15">
                      <td></td>
                      <td
                        colSpan={5}
                        className="pb-3 px-3 text-xs italic text-[#1B405B]"
                      >
                        Indicaciones: {it.instructions}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>

        {prescription.notes && (
          <section className="mt-6">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/70">
              Indicaciones / Notas
            </div>
            <div className="text-sm">{prescription.notes}</div>
          </section>
        )}

        <footer className="mt-16 pt-8 border-t border-[#1B405B]/30 flex justify-between items-end text-xs">
          <div className="flex flex-col items-start gap-1.5">
            <img
              src="/brand/consudoctor-icon.png"
              alt="ConsuDoctor"
              className="h-8 w-auto object-contain"
              data-testid="rx-footer-logo"
            />
            <div className="text-[#1B405B]/70 font-bold">
              www.consudoctor.com
            </div>
          </div>
          <div className="text-right">
            {doctor?.signature_url ? (
              <img
                src={doctor.signature_url}
                alt="Firma del médico"
                className="h-20 w-auto max-w-[240px] object-contain ml-auto"
                data-testid="rx-signature"
              />
            ) : (
              <div className="h-20" />
            )}
            <div className="border-t-2 border-[#163144] w-48 mb-1 ml-auto" />
            <div className="font-bold">{doctor?.name}</div>
            <div className="text-[#1B405B]/70">Firma del médico</div>
          </div>
        </footer>
      </div>
    </div>
  );
}
